import { isEmpty, isObject, isString, isUndefined, omit, omitBy } from 'lodash';
import { isCard, isColumn, isSketch } from '../../../../common/elements/utils/elementTypeUtils';
import { getContent, getElementType } from '../../../../common/elements/utils/elementPropertyUtils';
import { ImMNElement, MNElement, MNElementContent } from '../../../../common/elements/elementModelTypes';
import { getElementColorHexValue } from '../../../../common/colors/elementColorFormatUtil';
import { ColorObject } from '../../../../common/colors/colorObjectUtil';

export type ClipboardStyleChanges = Partial<MNElementContent>;
type CleanedClipboardStyleChanges = Omit<ClipboardStyleChanges, 'color'> & {
    color?: string | null;
};

/**
 * Clones style objects so clipboard changes don't retain references to the source content.
 */
const cloneElementStyleObject = <T extends object | null | undefined>(value: T): T => {
    if (!value || !isObject(value)) return value;
    return { ...value } as T;
};

/**
 * Picks the supported style fields from an element's content.
 */
const getStyleChangesFromContent = (content: MNElementContent): ClipboardStyleChanges =>
    omitBy(
        {
            background: cloneElementStyleObject(content.background),
            transparent: cloneElementStyleObject(content.transparent),
            color: content.color,
            secondaryColor: content.secondaryColor,
            autoColorIndex: content.autoColorIndex,
            startStyle: content.startStyle,
            endStyle: content.endStyle,
            lineStyle: content.lineStyle,
        },
        // Omits the transformation that matches this (i.e. if undefined, don't add it)
        isUndefined,
    ) as ClipboardStyleChanges;

/**
 * Converts non-string element colours into the plain string value we persist in clipboard changes.
 */
const getColorValue = (color: Exclude<MNElementContent['color'], string | null | undefined>) => {
    if ('value' in color && isString(color.value)) return color.value;

    return getElementColorHexValue(color as ColorObject);
};

/**
 * Returns the style-only clipboard changes from the source element for WEB-15461.
 */
export const getElementStyleChanges = (element?: MNElement | null): ClipboardStyleChanges | null => {
    const sourceContent = getContent(element);
    if (!sourceContent) return null;

    const contentChanges = getStyleChangesFromContent(sourceContent);
    if (isEmpty(contentChanges)) return null;

    return contentChanges;
};

type StyleMappingArgs = {
    sourceElement?: MNElement | null;
    targetElement?: ImMNElement | MNElement | null;
};

/**
 * If we're not copying between two lines, we want to strip out any line styles
 * as they won't be applicable to the target element and would just add noise to the style changes.
 */
const stripLineStyles = (changes: ClipboardStyleChanges): ClipboardStyleChanges =>
    omit(changes, ['startStyle', 'endStyle', 'lineStyle']) as ClipboardStyleChanges;

/**
 * If we're not copying between two colour swatches, we should change the color property
 * to be a simple string value.
 */
const mapColor = (changes: ClipboardStyleChanges): CleanedClipboardStyleChanges => {
    if (!changes.color || isString(changes.color)) return changes as CleanedClipboardStyleChanges;

    return {
        ...changes,
        color: getColorValue(changes.color),
    };
};

/**
 * Determines if an element supports both top strip and background colours.
 */
const supportsBackgroundColorStyle = (element?: ImMNElement | MNElement | null) =>
    isCard(element) || isColumn(element) || isSketch(element);

/**
 * Determines if an element supports transparency styling.
 */
const supportsTransparencyStyle = (element?: ImMNElement | MNElement | null) => isCard(element) || isSketch(element);

/**
 * Removes style fields that the target element type does not render.
 */
const stripUnsupportedTargetStyles = (
    changes: CleanedClipboardStyleChanges,
    targetElement?: ImMNElement | MNElement | null,
): CleanedClipboardStyleChanges => {
    if (supportsTransparencyStyle(targetElement)) return changes;

    return omit(changes, ['transparent']) as CleanedClipboardStyleChanges;
};

/**
 * If the source has a background color, prioritise that when copying styles to flat-colour element types.
 */
const mapFromBackgroundColorStyles = (sourceChanges: CleanedClipboardStyleChanges): CleanedClipboardStyleChanges => {
    const backgroundColor = sourceChanges.background?.color;
    const changes = omit(sourceChanges, ['background', 'transparent']) as CleanedClipboardStyleChanges;

    if (!backgroundColor) return changes;

    return { ...changes, color: backgroundColor };
};

/**
 * When copying styles from a flat-colour element to a background-capable element, map the color property
 * to the background colour, because that's most likely what the user intended.
 */
const mapToBackgroundColorStyles = (sourceChanges: CleanedClipboardStyleChanges): ClipboardStyleChanges => {
    const { color, ...changes } = sourceChanges;

    if (!color) return changes;

    return { ...changes, background: { color } };
};

/**
 * Applies the cross-type mappings needed when source and target element types differ.
 */
const getCrossTypeStyleChanges = ({
    sourceChanges,
    sourceElement,
    targetElement,
}: {
    sourceChanges: ClipboardStyleChanges;
    sourceElement?: MNElement | null;
    targetElement?: ImMNElement | MNElement | null;
}): ClipboardStyleChanges => {
    const strippedLineStyles = stripLineStyles(sourceChanges);
    const normalizedSourceChanges = mapColor(strippedLineStyles);
    const targetChanges = stripUnsupportedTargetStyles(normalizedSourceChanges, targetElement);

    if (supportsBackgroundColorStyle(sourceElement) && supportsBackgroundColorStyle(targetElement)) {
        return targetChanges;
    }

    // Copying from a background-capable element to flat-colour elements maps background color to color.
    if (supportsBackgroundColorStyle(sourceElement)) return mapFromBackgroundColorStyles(targetChanges);

    // Copying from flat-colour elements maps color onto the target background color.
    if (supportsBackgroundColorStyle(targetElement)) return mapToBackgroundColorStyles(targetChanges);

    return targetChanges;
};

/**
 * Maps source element styles onto a target element's style schema for WEB-15461.
 */
export const getMappedElementStyleChanges = ({
    sourceElement,
    targetElement,
}: StyleMappingArgs): ClipboardStyleChanges | null => {
    const sourceChanges = getElementStyleChanges(sourceElement);
    if (!sourceChanges) return null;

    const sourceType = getElementType(sourceElement);
    const targetType = getElementType(targetElement);

    // No mapping required
    if (sourceType === targetType) return sourceChanges;

    return getCrossTypeStyleChanges({
        sourceChanges,
        sourceElement,
        targetElement,
    });
};
